import { Suspense, useEffect, useRef, useState } from 'react'
import Debug from 'debug'
import { observer } from 'mobx-react-lite'
import { useQuery } from '@connectrpc/connect-query'
import { ErrorBoundary } from '@sentry/react'

import { getVideoPlaylistLink } from '@jotta/grpc-connect-openapi/photoQuery'
import { Box } from '@jotta/ui/Box'
import { lazyWithRetry } from '@jotta/ui/lazyWithRetry'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'

import type { CarouselPhoto } from '../../api/usePhoto'

import { usePhotoLargeThumb } from './usePhotoLargeThumb'

const debug = Debug('jotta:photos:MediaView')
const loadVideoPlayer = () =>
  import(/* webpackChunkName: "VideoPlayer" */ '@jotta/video')
const VideoPlayer = lazyWithRetry(loadVideoPlayer)

const nativeHlsSupported = (() => {
  if (typeof document === 'undefined') {
    return false
  }

  const video = document.createElement('video')
  return Boolean(
    video.canPlayType('application/vnd.apple.mpegurl') ||
    video.canPlayType('application/x-mpegURL'),
  )
})()

function playVideo(video: HTMLVideoElement) {
  void video.play().catch(() => undefined)
}

/** What a slide needs to show a photo — a timeline photo carries all of it. */
export type MediaViewItem = Pick<
  CarouselPhoto,
  'ref' | 'expiringSmallThumbUrl' | 'fileName' | 'videoDurationSeconds'
> & { isLivePhoto?: boolean }

export interface MediaViewProps {
  item: MediaViewItem
  active?: boolean
  /**
   * Set while the viewer is stepping quickly: only what is already cached is shown, and
   * live photos don't start playing for a photo that is passed straight away.
   */
  scrubbing?: boolean
  slideShowMode?: boolean
  setPlaying?: (active: boolean) => void
}

/**
 * The small preview and the LARGE thumbnail stacked in the same box, so the preview
 * fills the slide exactly like the large one and is simply covered once the large one
 * has painted, instead of the image resizing when the source changes.
 */
function ThumbnailStack({
  smallUrl,
  largeUrl,
}: {
  smallUrl: string
  largeUrl: string | undefined
}) {
  return (
    <div className="relative size-full">
      <img
        alt=""
        src={smallUrl}
        className="size-full object-contain"
        draggable={false}
        onDragStart={e => e.preventDefault()}
      />
      {largeUrl && (
        <img
          alt=""
          src={largeUrl}
          className="absolute inset-0 size-full object-contain"
          draggable={false}
          onDragStart={e => e.preventDefault()}
        />
      )}
      <Box variant="layout.mediaItemBackground" />
    </div>
  )
}

export const MediaView = observer<MediaViewProps>(function MediaView({
  item,
  active = false,
  scrubbing = false,
  slideShowMode,
  setPlaying,
}) {
  const { expiringSmallThumbUrl, ref, fileName, isLivePhoto } = item
  const livePhotoRef = useRef<HTMLVideoElement>(null)
  const [livePhotoEnded, setLivePhotoEnded] = useState(false)
  const [livePhotoPlaylistFailed, setLivePhotoPlaylistFailed] = useState(false)
  const { url: largeThumbUrl } = usePhotoLargeThumb(ref, {
    enabled: !scrubbing,
  })
  const thumbUrl = largeThumbUrl ?? expiringSmallThumbUrl
  const isVideo = Boolean(item.videoDurationSeconds) && !isLivePhoto
  const thumbnails = (
    <ThumbnailStack smallUrl={expiringSmallThumbUrl} largeUrl={largeThumbUrl} />
  )

  // Start fetching the player's code with the slide, so opening a video doesn't
  // wait for the chunk after the playlist link has already arrived.
  useEffect(() => {
    if (isVideo) {
      void loadVideoPlayer()
    }
  }, [isVideo])

  const { data: videoPlayListURLs } = useQuery(
    getVideoPlaylistLink,
    {
      ref,
    },
    {
      refetchOnMount: false,
      staleTime: 300_000,
      enabled:
        Boolean(item.videoDurationSeconds || item.isLivePhoto) && !scrubbing,
      select: data => {
        const result = data.result
        switch (result?.case) {
          case 'playlistLink':
            return result.value
          case undefined:
          case 'videoNotFound':
            return undefined
          default:
            return exhaustiveGuard(result)
        }
      },
    },
  )
  const videoUrlOriginal = videoPlayListURLs?.urlOriginal
  const videoUrlPlaylist = videoPlayListURLs?.urlPlaylist
  const livePhotoVideoUrl =
    nativeHlsSupported && !livePhotoPlaylistFailed
      ? (videoUrlPlaylist ?? videoUrlOriginal)
      : videoUrlOriginal

  useEffect(() => {
    setLivePhotoEnded(false)
    setLivePhotoPlaylistFailed(false)
  }, [active, videoUrlOriginal, videoUrlPlaylist])

  useEffect(() => {
    if (
      !isLivePhoto ||
      !active ||
      scrubbing ||
      livePhotoEnded ||
      !livePhotoVideoUrl
    ) {
      return
    }

    const video = livePhotoRef.current
    if (!video) {
      return
    }

    // Safari is strict about autoplay and source selection for live photos.
    // Set inline/muted playback and the chosen src before loading and playing.
    video.muted = true
    video.playsInline = true
    video.setAttribute('playsinline', '')
    video.setAttribute('webkit-playsinline', '')
    video.src = livePhotoVideoUrl
    video.load()
  }, [active, isLivePhoto, livePhotoEnded, livePhotoVideoUrl, scrubbing])

  if (!isLivePhoto && videoUrlOriginal) {
    return (
      <div className="flex size-full flex-auto py-16">
        <ErrorBoundary
          beforeCapture={scope => {
            scope.setTag('reactComponent', 'VideoPlayer')
            scope.setTag('jottaModule', 'MediaCarousel')
          }}
          onError={(error, componentStack, eventId) => {
            debug(error, componentStack, eventId)
          }}
        >
          <Suspense fallback={thumbnails}>
            <VideoPlayer
              active={active}
              playListURL={videoUrlPlaylist}
              videoURL={videoUrlOriginal}
              poster={thumbUrl}
              autoPlay={slideShowMode}
              name={fileName}
              rewindOnEnd
              playerProps={{
                onPlay: () => setPlaying && setPlaying(true),
                onPause: () => setPlaying && setPlaying(false),
                onEnded: () => setPlaying && setPlaying(false),
              }}
            />
          </Suspense>
        </ErrorBoundary>
      </div>
    )
  } else if (
    isLivePhoto &&
    active &&
    !scrubbing &&
    !livePhotoEnded &&
    livePhotoVideoUrl
  ) {
    return (
      <video
        ref={livePhotoRef}
        // Sized like the stills: unsized, a small poster would show at its own size.
        className="size-full object-contain"
        key={livePhotoVideoUrl}
        poster={thumbUrl}
        autoPlay
        draggable={false}
        disablePictureInPicture
        playsInline
        muted
        onLoadedMetadata={event => {
          playVideo(event.currentTarget)
        }}
        onEnded={() => setLivePhotoEnded(true)}
        onError={() => {
          if (livePhotoVideoUrl === videoUrlPlaylist && videoUrlOriginal) {
            setLivePhotoPlaylistFailed(true)
            return
          }
          setLivePhotoEnded(true)
        }}
      />
    )
  } else if (isVideo) {
    // Same box as the player below, so nothing resizes when the player takes over.
    return <div className="flex size-full flex-auto py-16">{thumbnails}</div>
  } else {
    return thumbnails
  }
})
